import React, { useEffect } from 'react';
import { Trash2, AlertCircle } from 'lucide-react';

const DeleteModal = ({ 
  isOpen, 
  onClose, 
  onDelete, 
  title, 
  itemName, 
  isLoading = false 
}) => {
  // Handle escape key press
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape' && isOpen) {
        onClose();
      }
    };

    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [isOpen, onClose]);

  // Handle body scroll
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isOpen]);

  if (!isOpen) return null;

  return (
    <div className="overflow-y-auto fixed inset-0 z-50">
      {/* Enhanced backdrop with blur effect */}
      <div 
        className="fixed inset-0 backdrop-blur-sm transition-all duration-300 bg-black/40"
        onClick={onClose}
      />

      {/* Modal */}
      <div className="flex justify-center items-center p-4 min-h-full">
        <div 
          className="overflow-hidden relative w-full max-w-md bg-white rounded-xl shadow-2xl opacity-100 transition-all duration-200 transform scale-100 animate-modal-enter"
          onClick={(e) => e.stopPropagation()}
        >
          {/* Enhanced Header */}
          <div className="p-5 border-b border-gray-200">
            <div className="flex gap-3 items-center text-gray-800">
              <div className="p-2 bg-red-100 rounded-lg">
                <Trash2 className="w-5 h-5 text-red-600" />
              </div>
              <span className="text-base font-semibold">{title || 'Delete Item'}</span>
            </div>
          </div>

          {/* Enhanced Content */}
          <div className="p-6 space-y-6">
            <div className="flex flex-col items-center space-y-4 text-center">
              <div className="flex justify-center items-center w-16 h-16 bg-red-50 rounded-full ring-8 ring-red-50">
                <AlertCircle className="w-8 h-8 text-red-600" />
              </div>
              <div className="space-y-2">
                <h3 className="text-lg font-semibold text-gray-900">
                  Are you absolutely sure?
                </h3>
                <p className="max-w-sm text-sm text-gray-500">
                  Are you sure you want to delete{" "}
                  <span className="font-medium text-gray-800">
                    "{itemName}"
                  </span>
                  ? This action cannot be undone and all associated data will be permanently removed.
                </p>
              </div>
            </div>
            
            {/* Enhanced Buttons */}
            <div className="flex gap-3 justify-end pt-2">
              <button
                onClick={onClose}
                disabled={isLoading}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white rounded-lg border border-gray-300 transition-colors duration-200 hover:bg-gray-50 hover:border-gray-400 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500 disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                onClick={onDelete}
                disabled={isLoading}
                className="flex gap-2 items-center px-4 py-2 text-sm font-medium text-white bg-red-600 rounded-lg border border-transparent shadow-sm transition-colors duration-200 hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 disabled:opacity-50"
              >
                {isLoading ? (
                  <>
                    <span className="w-4 h-4 rounded-full border-2 border-white animate-spin border-t-transparent"></span>
                    <span>Deleting...</span>
                  </>
                ) : (
                  <>
                    <Trash2 className="w-4 h-4" />
                    <span>Delete</span>
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DeleteModal; 